import { Component, OnInit, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { UserService } from 'src/app/services/user.service';
import { API } from 'src/environments/environment';

interface Opcao {
  id: number;
  descricao: string;
  dataEmissao?: string | null;
}

interface FornecedorOpcao extends Opcao {
  idPlanoContas?: number | null;
}

@Component({
  selector: 'app-contas-pagar',
  templateUrl: './contas-pagar.component.html',
  styleUrls: ['./contas-pagar.component.scss']
})
export class ContasPagarComponent implements OnInit {
  id = 0;
  ehPagamento = false;
  private carregandoRegistro = false;

  idFinDocumento = 0;
  parcela = 1;
  idFinFornecedorRecebedor = 0;
  idCentroCusto = 0;
  idPlanoContas = 0;
  previsaoReal = 'R';
  dataEmissao = '';
  dataVencimento = '';
  dataPagamento = '';
  idTipoPagamento = 0;
  valorImposto: number | null = null;
  valorDocumento: number | null = null;
  valorOutrasDespesas: number | null = null;
  valorDesconto: number | null = null;
  valorMulta: number | null = null;
  valorCustas: number | null = null;
  valorPago: number | null = null;
  valorJuros: number | null = null;
  taxaJuroSdia: number | null = null;
  valorCustasParcial: number | null = null;
  observacao = '';
  pago = false;
  banco: number | null = null;

  documentos: Opcao[] = [];
  fornecedores: FornecedorOpcao[] = [];
  centrosCustos: Opcao[] = [];
  planosContas: Opcao[] = [];
  tiposPagamento: Opcao[] = [];
  bancos: Opcao[] = [];
  novoTipoPagamentoDescricao = '';
  novoBancoDescricao = '';
  salvandoBanco = false;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.carregarOpcoes();
    this.carregarCentrosCustos();
    this.route.queryParamMap.subscribe((params) => {
      this.id = Number(params.get('id') || 0);
      const pagamentoParam = (params.get('pagamento') || '').toLowerCase();
      this.ehPagamento = pagamentoParam === 'true' || pagamentoParam === '1';
      if (this.id > 0) this.carregar();
      else this.novo();
    });
  }

  novo(): void {
    this.id = 0;
    this.idFinDocumento = 0;
    this.parcela = 1;
    this.idFinFornecedorRecebedor = 0;
    this.idCentroCusto = 0;
    this.idPlanoContas = 0;
    this.previsaoReal = 'R';
    this.dataEmissao = '';
    this.dataVencimento = '';
    this.dataPagamento = '';
    this.idTipoPagamento = 0;
    this.valorImposto = null;
    this.valorDocumento = null;
    this.valorOutrasDespesas = null;
    this.valorDesconto = null;
    this.valorMulta = null;
    this.valorCustas = null;
    this.valorPago = null;
    this.valorJuros = null;
    this.taxaJuroSdia = null;
    this.valorCustasParcial = null;
    this.observacao = '';
    this.pago = false;
    this.banco = null;
  }

  private carregarCentrosCustos(): void {
    this.ajaxService.get(`${API}financeiro/centrocustos`).subscribe({
      next: (response) => {
        if (response?.hasErrors) {
          return;
        }
        this.centrosCustos = this.util.mapearOpcoesCentroCusto(this.util.extrairListaResponse(response));
      }
    });
  }

  carregarOpcoes(): void {
    this.ajaxService.get(`${API}financeiro/FinContaPagar/ConsultarOpcoes`).subscribe({
      next: (response) => {
        const data = this.desempacotarResponse(response);
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível carregar as opções.');
          return;
        }

        const documentosData = data.documentos || data.docs || [];
        const fornecedoresData = data.fornecedores || data.fornecedor || [];
        const centrosCustosData = data.centroscustos || data.centros_custos || data.centros || [];
        const tiposPagamentoData = data.tipospagamento || data.tipopagamento || data.tiposPagamento || [];
        const bancosData = data.bancos || [];

        this.documentos = this.normalizarLista(documentosData)
          .map((x: any) => ({
            id: Number(x.id || 0),
            descricao: String(x.descricao || ''),
            dataEmissao: this.extrairDataEmissao(x)
          }))
          .filter((x: Opcao) => x.id > 0)
          .sort((a: Opcao, b: Opcao) => this.compararPorDataEmissaoDesc(a, b));
        this.fornecedores = this.normalizarLista(fornecedoresData)
          .map((x: any) => ({
            id: Number(x.id || 0),
            descricao: String(x.descricao || ''),
            idPlanoContas: x.idplanocontas != null ? Number(x.idplanocontas) : null
          }))
          .filter((x: Opcao) => x.id > 0);
        this.centrosCustos = this.util.mapearOpcoesCentroCusto(this.normalizarLista(centrosCustosData));
        this.tiposPagamento = this.mapearTiposPagamento(tiposPagamentoData);
        this.bancos = this.normalizarLista(bancosData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x: Opcao) => x.id > 0);

        this.carregarPlanosContasEntradaAnalitica();
        this.aplicarPlanoContasDoFornecedorSeVazio();
      },
      error: () => {
        this.util.toasterShowError('Erro', 'Falha ao carregar Documento, Fornecedor e Tipo de Pagamento.');
      }
    });
  }

  private mapearTiposPagamento(data: any): Opcao[] {
    return this.normalizarLista(data)
      .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
      .filter((x: Opcao) => x.id > 0);
  }

  private carregarTiposPagamento(idSelecionar?: number, descricaoSelecionar?: string): void {
    this.ajaxService.get(`${API}financeiro/TipoPagamento/Consultar`).subscribe({
      next: (response) => {
        if (response?.hasErrors) return;
        this.tiposPagamento = this.mapearTiposPagamento(this.util.extrairListaResponse(response));
        const descricao = (descricaoSelecionar || '').trim().toLowerCase();
        const selecionado = this.tiposPagamento.find((x) => x.id === idSelecionar)
          || this.tiposPagamento.find((x) => !!descricao && x.descricao.trim().toLowerCase() === descricao);
        if (selecionado) this.idTipoPagamento = selecionado.id;
      }
    });
  }

  abrirModalTipoPagamento(content: TemplateRef<any>): void {
    this.novoTipoPagamentoDescricao = '';
    this.modalService.open(content);
  }

  salvarTipoPagamento(modal: NgbActiveModal): void {
    const descricao = String(this.novoTipoPagamentoDescricao || '').trim();
    if (descricao.length < 2) {
      this.util.toasterShowError('Atenção', 'Informe uma descrição válida.');
      return;
    }

    this.util.showLoading();
    this.ajaxService.post(`${API}financeiro/TipoPagamento/Salvar`, { id: 0, descricao }).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar o tipo de pagamento.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Tipo de pagamento cadastrado!');
        modal.close();
        this.carregarTiposPagamento(Number(response?.data?.id || 0), descricao);
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar tipo de pagamento.');
      }
    });
  }

  abrirModalBanco(content: TemplateRef<any>): void {
    this.novoBancoDescricao = '';
    this.modalService.open(content);
  }

  private carregarBancos(idSelecionar?: number, descricaoSelecionar?: string): void {
    this.ajaxService.get(`${API}financeiro/FinBanco/Consultar`).subscribe({
      next: (response) => {
        if (response?.hasErrors) return;
        this.bancos = this.normalizarLista(this.util.extrairListaResponse(response))
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x: Opcao) => x.id > 0);
        const descricao = (descricaoSelecionar || '').trim().toLowerCase();
        const selecionado = this.bancos.find((x) => x.id === idSelecionar)
          || this.bancos.find((x) => !!descricao && x.descricao.trim().toLowerCase() === descricao);
        if (selecionado) this.banco = selecionado.id;
      }
    });
  }

  salvarBanco(modal: NgbActiveModal): void {
    const descricao = String(this.novoBancoDescricao || '').trim();
    if (descricao.length < 2) {
      this.util.toasterShowError('Atenção', 'Informe um nome de banco válido.');
      return;
    }
    this.salvandoBanco = true;
    this.util.showLoading();
    this.ajaxService.post(`${API}financeiro/FinBanco/Salvar`, { id: 0, descricao }).subscribe({
      next: (response) => {
        this.salvandoBanco = false;
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar o banco.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Banco cadastrado!');
        modal.close();
        this.carregarBancos(Number(response?.data?.id || 0), descricao);
      },
      error: () => {
        this.salvandoBanco = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar banco.');
      }
    });
  }

  private carregarPlanosContasEntradaAnalitica(): void {
    this.ajaxService.get(`${API}financeiro/FinContaPagar/ConsultarPlanosContasEntradaAnalitica`).subscribe({
      next: (response) => {
        if (response?.hasErrors) return;
        const data = this.desempacotarResponse(response);
        this.planosContas = this.normalizarLista(data)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x: Opcao) => x.id > 0);
        this.aplicarPlanoContasDoFornecedorSeVazio();
      },
      error: () => {
        this.planosContas = [];
      }
    });
  }

  carregar(): void {
    this.carregandoRegistro = true;
    this.util.showLoading();
    this.ajaxService.get(`${API}financeiro/FinContaPagar/Obter?id=${this.id}`).subscribe({
      next: (response) => {
        this.util.closeLoading();
        const data = this.desempacotarResponse(response);
        this.idFinDocumento = Number(data.idfindocumento || 0);
        this.parcela = Number(data.parcela || 1);
        this.idFinFornecedorRecebedor = Number(data.idfinfornecedorrecebedor || 0);
        this.idCentroCusto = Number(data.idcentrocusto || 0);
        this.idPlanoContas = Number(
          data.idplanocontas ||
          data.idplanoconta ||
          data.id_plano_contas ||
          0
        );
        this.previsaoReal = String(data.previsaoreal || 'R').toUpperCase() === 'P' ? 'P' : 'R';
        if (this.idPlanoContas <= 0) {
          this.aplicarPlanoContasDoFornecedor();
          this.carregarPlanoContasFornecedor();
        }
        this.dataEmissao = this.toDateInput(data.dataemissao);
        this.dataVencimento = this.toDateInput(data.datavencimento);
        this.dataPagamento = this.toDateInput(data.datapagamento);
        this.idTipoPagamento = Number(data.idtipopagamento || 0);
        this.valorImposto = data.valorimposto != null ? Number(data.valorimposto) : null;
        this.valorDocumento = data.valordocumento != null ? Number(data.valordocumento) : null;
        this.valorOutrasDespesas = data.valoroutrasdespesas != null ? Number(data.valoroutrasdespesas) : null;
        this.valorDesconto = data.valordesconto != null ? Number(data.valordesconto) : null;
        this.valorMulta = data.valormulta != null ? Number(data.valormulta) : null;
        this.valorCustas = data.valorcustas != null ? Number(data.valorcustas) : null;
        this.valorPago = data.valorpago != null ? Number(data.valorpago) : null;
        this.valorJuros = data.valorjuros != null ? Number(data.valorjuros) : null;
        this.taxaJuroSdia = data.taxajurosdia != null ? Number(data.taxajurosdia) : null;
        this.valorCustasParcial = data.valorcustasparcial != null ? Number(data.valorcustasparcial) : null;
        this.observacao = String(data.observacao || '');
        this.pago = Boolean(data.pago);
        this.banco = data.idfinbanco != null && Number(data.idfinbanco) > 0 ? Number(data.idfinbanco) : null;
        this.carregandoRegistro = false;
      },
      error: () => {
        this.util.closeLoading();
        this.carregandoRegistro = false;
        this.util.toasterShowError('Erro', 'Não foi possível carregar a conta a pagar.');
      }
    });
  }

  calcularValorPago(): void {
    const payload = {
      valorImposto: this.valorImposto,
      valorDocumento: this.valorDocumento,
      valorOutrasDespesas: this.valorOutrasDespesas,
      valorDesconto: this.valorDesconto,
      valorMulta: this.valorMulta,
      valorCustas: this.valorCustas,
      valorJuros: this.valorJuros,
      valorCustasParcial: this.valorCustasParcial
    };

    this.ajaxService.post(`${API}financeiro/FinContaPagar/CalcularValorPago`, payload).subscribe({
      next: (response) => {
        const data = this.desempacotarResponse(response);
        const valor = Number(data);
        if (!Number.isNaN(valor)) this.valorPago = valor;
      }
    });
  }

  salvar(): void {
    if (!this.idFinDocumento || this.idFinDocumento <= 0) {
      this.util.toasterShowError('Atenção', 'Documento é obrigatório.');
      return;
    }
    if (!this.idFinFornecedorRecebedor || this.idFinFornecedorRecebedor <= 0) {
      this.util.toasterShowError('Atenção', 'Fornecedor é obrigatório.');
      return;
    }
    if (!this.idPlanoContas || this.idPlanoContas <= 0) {
      this.util.toasterShowError('Atenção', 'Plano de Contas é obrigatório. Selecione uma opção da lista.');
      return;
    }
    if (!this.dataEmissao) {
      this.util.toasterShowError('Atenção', 'Data de emissão é obrigatória.');
      return;
    }
    if (this.valorDocumento == null) {
      this.util.toasterShowError('Atenção', 'Valor do documento é obrigatório.');
      return;
    }
    if (this.ehPagamento && (!this.dataPagamento || this.valorPago == null)) {
      this.util.toasterShowError('Atenção', 'Para pagamento, informe Data de Pagamento e Valor Pago.');
      return;
    }
    const idUsuarioLogado = this.getIdUsuarioLogado();
    if (!idUsuarioLogado) {
      this.util.toasterShowError('Atenção', 'Não foi possível identificar o usuário logado.');
      return;
    }

    const payload = {
      id: this.id || 0,
      idUsuarioLogado,
      idFinDocumento: this.idFinDocumento,
      parcela: this.parcela || 1,
      idFinFornecedorRecebedor: this.idFinFornecedorRecebedor,
      idCentroCusto: this.idCentroCusto > 0 ? this.idCentroCusto : null,
      idPlanoContas: this.idPlanoContas,
      previsaoReal: this.previsaoReal === 'P' ? 'P' : 'R',
      dataEmissao: this.dataEmissao || null,
      dataVencimento: this.dataVencimento || null,
      dataPagamento: this.dataPagamento || null,
      idTipoPagamento: this.idTipoPagamento > 0 ? this.idTipoPagamento : null,
      valorImposto: this.valorImposto,
      valorDocumento: this.valorDocumento,
      valorOutrasDespesas: this.valorOutrasDespesas,
      valorDesconto: this.valorDesconto,
      valorMulta: this.valorMulta,
      valorCustas: this.valorCustas,
      valorPago: this.valorPago,
      valorJuros: this.valorJuros,
      pago: this.pago,
      taxaJuroSdia: this.taxaJuroSdia,
      valorCustasParcial: this.valorCustasParcial,
      observacao: this.observacao,
      ehPagamento: this.ehPagamento,
      banco: this.banco
    };

    this.util.showLoading();
    this.ajaxService.post(`${API}financeiro/FinContaPagar/Salvar`, payload).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', this.id > 0 ? 'Conta a pagar atualizada com sucesso!' : 'Conta a pagar cadastrada com sucesso!');
        this.router.navigate(['/consultar-contas-pagar']);
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar conta a pagar.');
      }
    });
  }

  voltar(): void {
    this.router.navigate(['/consultar-contas-pagar']);
  }

  onFornecedorChange(): void {
    if (this.carregandoRegistro) return;
    this.aplicarPlanoContasDoFornecedor();
    this.carregarPlanoContasFornecedor();
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'descricao')) return [data];
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private getIdUsuarioLogado(): number | null {
    const userData = this.userService.getUserData();
    if (!userData) return null;

    const userId =
      Number(userData.User?.Id) ||
      Number((userData as any)?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid']) ||
      0;

    return userId > 0 ? userId : null;
  }

  private aplicarPlanoContasDoFornecedor(): void {
    if (!this.idFinFornecedorRecebedor || this.idFinFornecedorRecebedor <= 0) {
      this.idPlanoContas = 0;
      return;
    }

    const fornecedorSelecionado = this.fornecedores.find((x) => x.id === this.idFinFornecedorRecebedor);
    const idPlanoContas = Number(
      fornecedorSelecionado?.idPlanoContas ||
      (fornecedorSelecionado as any)?.idplanocontas ||
      (fornecedorSelecionado as any)?.id_plano_contas ||
      0
    );
    this.idPlanoContas = idPlanoContas > 0 ? idPlanoContas : 0;
  }

  private aplicarPlanoContasDoFornecedorSeVazio(): void {
    if (this.idPlanoContas > 0) {
      return;
    }
    this.aplicarPlanoContasDoFornecedor();
  }

  private carregarPlanoContasFornecedor(): void {
    if (!this.idFinFornecedorRecebedor || this.idFinFornecedorRecebedor <= 0) {
      return;
    }

    this.ajaxService.get(`${API}financeiro/FinFornecedor/Obter?id=${this.idFinFornecedorRecebedor}`).subscribe({
      next: (response) => {
        if (response?.hasErrors) return;
        const data = this.desempacotarResponse(response);
        const idPlanoContas = Number(data.idplanocontas || data.id_plano_contas || 0);
        if (idPlanoContas > 0 && this.idPlanoContas <= 0) {
          this.idPlanoContas = idPlanoContas;
        }
      },
      error: () => {
        // Mantém fallback local sem bloquear usuário.
      }
    });
  }

  private extrairDataEmissao(item: any): string | null {
    const dataDireta = String(
      item?.dataemissao ||
      item?.dataEmissao ||
      item?.dtEmissao ||
      item?.data_emissao ||
      ''
    ).trim();

    if (dataDireta) return dataDireta;

    const descricao = String(item?.descricao || '');
    const dataDaDescricao = this.extrairDataDaDescricao(descricao);
    return dataDaDescricao || null;
  }

  private compararPorDataEmissaoDesc(a: Opcao, b: Opcao): number {
    const timestampA = this.converterDataParaTimestamp(a?.dataEmissao);
    const timestampB = this.converterDataParaTimestamp(b?.dataEmissao);
    if (timestampA !== timestampB) return timestampB - timestampA;
    return String(a?.descricao || '').localeCompare(String(b?.descricao || ''));
  }

  private extrairDataDaDescricao(texto: string): string {
    const valor = String(texto || '');
    const matchBr = valor.match(/\b(\d{2}\/\d{2}\/\d{4})\b/);
    if (matchBr?.[1]) return matchBr[1];
    const matchIso = valor.match(/\b(\d{4}-\d{2}-\d{2})\b/);
    if (matchIso?.[1]) return matchIso[1];
    return '';
  }

  private converterDataParaTimestamp(dataTexto: string | null | undefined): number {
    const valor = String(dataTexto || '').trim();
    if (!valor) return 0;

    const br = valor.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (br) {
      const dia = Number(br[1]);
      const mes = Number(br[2]);
      const ano = Number(br[3]);
      return new Date(ano, mes - 1, dia).getTime();
    }

    const iso = valor.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      const ano = Number(iso[1]);
      const mes = Number(iso[2]);
      const dia = Number(iso[3]);
      return new Date(ano, mes - 1, dia).getTime();
    }

    const parsePadrao = new Date(valor).getTime();
    return Number.isFinite(parsePadrao) ? parsePadrao : 0;
  }
}
